import React, { useEffect, useState } from "react";
import { BsHouse } from "react-icons/bs";
import styles from "./Navbar.module.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FiLogOut, FiMenu, FiUser, FiUserPlus, FiDollarSign, FiBookOpen, FiCompass } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { logout, setLanguage } from "../../redux/features/authSlice";
import { Button, Dropdown, message, Switch, Tooltip } from "antd";
import avatar from "../../assets/userAvatar.png";
import { FaAngleDown } from "react-icons/fa6";
import { FaPhoneAlt } from "react-icons/fa";
import { MdClose, MdOutlineDashboard, MdOutlineDraw } from "react-icons/md";
import logoImg from "../../assets/logo.png";
import logoLightImg from "../../assets/logoLight.png";
import { getTranslation } from "../../util/utilFunc";
import { UserOutlined } from "@ant-design/icons";
import { setUserGuideOpen, setUserGuideEnabled } from "../../redux/features/authSlice";
import UserPreferencesServices from "../../services/userPreferencesServices";

const seoExploreLinks = [
  {
    key: "innovation-software",
    label: "AI Innovation Software",
    to: "/ai-innovation-management-software",
  },
  {
    key: "portfolio-management",
    label: "Portfolio Management",
    to: "/innovation-portfolio-management",
  },
  {
    key: "team-solutions",
    label: "Team Solutions",
    to: "/solutions/innovation-teams",
  },
  {
    key: "templates",
    label: "Templates",
    to: "/templates/idea-evaluation-template",
  },
  {
    key: "compare",
    label: "Compare Alternatives",
    to: "/compare/clickup-alternative",
  },
  {
    key: "learn",
    label: "Learn",
    to: "/learn/how-to-evaluate-business-ideas",
  },
  {
    key: "industries",
    label: "Industries",
    to: "/innovation-management-for-healthcare",
  },
  {
    key: "glossary",
    label: "Glossary",
    to: "/glossary/innovation-management",
  },
  {
    key: "frameworks",
    label: "Frameworks",
    to: "/dmaic-guide",
  },
  {
    key: "workflows",
    label: "Workflows",
    to: "/workflow/annual-innovation-cycle",
  },
];

function Navbar({ textTheme }) {
  const { userInfo, language, userGuideEnabled } = useSelector((state) => state.auth);

  const { first_name, last_name } = userInfo || {};
  const [isToggle, setIsToggle] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  const isBlogPage = location.pathname.startsWith('/blogs');
  // Decide text color scheme at top of page (desktop):
  // - If scrolled or mobile: use scrolledNav (white bg, dark text)
  // - Else (top, desktop):
  //     * If textTheme forces dark (true or 'dark') or on blog pages (typically light hero): darkText
  //     * Otherwise (landing with dark/imagery hero): lightText
  const forceDark = textTheme === true || textTheme === 'dark';

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
    message.success(getTranslation("Logged out successfully!", language));
  };

  const handleChangeLang = (isArabic) => {
    // console.log(isArabic);
    const language = isArabic ? "ar" : "en";
    dispatch(setLanguage(language));
  };

  const exploreMenuItems = seoExploreLinks.map((item) => ({
    key: item.key,
    label: <Link to={item.to}>{item.label}</Link>,
  }));

  const itemsProfile = [
    {
      key: "1",
      icon: <MdOutlineDashboard style={{ fontSize: "1.2rem" }} />,
      label: (
        <Link
          onClick={() => localStorage.setItem("selectedKey", "1")}
          to={getDashboardUrl()}
        >
          {getTranslation("My Dashboard", language)}
        </Link>
      ),
    },
    {
      key: "2",
      icon: <UserOutlined style={{ fontSize: "1.2rem" }} />,
      label: (
        <Link to={getProfileUrl()}>
          {getTranslation("My Profile", language)}
        </Link>
      ),
    },
    {
      key: "3",
      icon: (
        <MdOutlineDraw
          style={{ fontSize: "1.2rem" }}
          className={styles.nav_icon}
        />
      ),
      label: (
        <Link to={"/dashboard/my-drawings"}>{getTranslation("My Drawing", language)}</Link>
      ),
    },
    {
      key: "user-guide-open",
      icon: <FiBookOpen style={{ fontSize: "1.2rem" }} />,
      label: (
        <div onClick={() => dispatch(setUserGuideOpen(true))}>
          {getTranslation("Open User Guide", language)}
        </div>
      ),
    },
    !userGuideEnabled && {
      key: "user-guide-enable",
      icon: <FiBookOpen style={{ fontSize: "1.2rem" }} />,
      label: (
        <div onClick={async () => {
          try {
            const { data } = await UserPreferencesServices.updateUserGuidePreference(true);
            dispatch(setUserGuideEnabled(true));
            if (data?.message) message.success(data.message);
          } catch (error) {
            const backendMsg = error?.response?.data?.message || error?.response?.data?.error;
            if (backendMsg) {
              message.error(backendMsg);
            }
          }
        }}>
          {getTranslation("Enable User Guide on startup", language)}
        </div>
      ),
    },

    {
      key: "4",
      danger: true,
      icon: <FiLogOut style={{ fontSize: "1.2rem" }} />,
      label: (
        <div onClick={handleLogout}>{getTranslation("Logout", language)}</div>
      ),
    },
  ];

  function getDashboardUrl() {
    let url = "/dashboard";
    if (userInfo?.usertype === "superadmin") {
      url = "/superadmin/dashboard";
    }

    return url;
  }

  function getProfileUrl() {
    let url = "/dashboard/my-profile";
    if (userInfo?.usertype === "superadmin") {
      url = "/superadmin/dashboard/my-profile";
    }

    return url;
  }

  // Always use white navbar with dark text (no transparent state)
  const navClassName = [styles.navbar, styles.scrolledNav].join(' ');

  return (
    <nav className={navClassName}>
      <div className={styles.logo__wrapper}>
        <div className={styles.logo__item}>
          {/* Always use the standard logo to match dark text on white navbar */}
          <img src={logoImg} alt="." />
          <h3>Compelion</h3>
        </div>
        {isToggle ? (
          <MdClose
            onClick={() => setIsToggle(false)}
            className={`${styles.toggler} ${styles.toggleIconClose}`}
          />
        ) : (
          <FiMenu
            onClick={() => setIsToggle(true)}
            className={`${styles.toggler} ${styles.toggleIconClose}`}
          />
        )}
      </div>
      <ul
        className={`${styles.navbar__links} ${isToggle ? styles.toggled__navList : ""
          }`}
      >
        {isToggle && (
          <li
            style={{ textAlign: "right", margin: "10px 0px" }}
            className={styles.notification__wrapper}
          >
            <Tooltip
              title={language === "ar" ? "Change Language?" : "تغيير اللغة"}
            >
              <Switch
                checked={language === "ar"}
                onChange={handleChangeLang}
                checkedChildren="English"
                unCheckedChildren="عربي"
              />
            </Tooltip>
          </li>
        )}

        <li>
          <Link to="/">
            <BsHouse className={styles.nav_icon} />
            <span>{getTranslation("Home", language)}</span>
          </Link>
        </li>
        <li>
          <Link to="/about-us">
            <FiUser className={styles.nav_icon} />
            <span>{getTranslation("About", language)}</span>
          </Link>
        </li>
        {isToggle ? (
          <li className={styles.mobileExploreSection}>
            <div className={styles.mobileExploreHeading}>
              <FiCompass className={styles.nav_icon} />
              <span>{getTranslation("Explore", language)}</span>
            </div>
            <div className={styles.mobileExploreLinks}>
              {seoExploreLinks.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  onClick={() => setIsToggle(false)}
                  className={styles.mobileExploreLink}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </li>
        ) : (
          <li>
            <Dropdown
              menu={{ items: exploreMenuItems }}
              placement="bottom"
              trigger={["hover", "click"]}
            >
              <button type="button" className={styles.navDropdownTrigger}>
                <FiCompass className={styles.nav_icon} />
                <span>{getTranslation("Explore", language)}</span>
                <FaAngleDown className={styles.dropdownCaret} />
              </button>
            </Dropdown>
          </li>
        )}
        <li>
          <Link to="/contact-us">
            <FaPhoneAlt className={styles.nav_icon} />
            <span>{getTranslation("Contact", language)}</span>
          </Link>
        </li>
        <li>
          <Link to="/blogs">
            <FiBookOpen className={styles.nav_icon} />
            <span>{getTranslation("Blogs", language)}</span>
          </Link>
        </li>
        <li>
          <Link to="/pricing">
            <FiDollarSign className={styles.nav_icon} />
            <span>{getTranslation("Pricing", language)}</span>
          </Link>
        </li>
        {userInfo && isToggle && (
          <>
            <li>
              <Link to={getDashboardUrl()}>
                <MdOutlineDashboard
                  style={{ fontSize: "1.2rem" }}
                  className={styles.nav_icon}
                />
                <span>{getTranslation("My Dashboard", language)}</span>
              </Link>
            </li>
            <li>
              <Link to={getProfileUrl()}>
                <UserOutlined
                  style={{ fontSize: "1.2rem" }}
                  className={styles.nav_icon}
                />
                <span> {getTranslation("My Profile", language)}</span>
              </Link>
            </li>
            <li>
              <Link to={"/dashboard/my-drawings"}>
                <MdOutlineDraw
                  style={{ fontSize: "1.2rem" }}
                  className={styles.nav_icon}
                />
                <span>{getTranslation("Drawing", language)}</span>
              </Link>
            </li>

            <li>
              <Button
                style={{ margin: "10px", width: "100%" }}
                danger
                onClick={handleLogout}
              >
                <FiLogOut className={styles.nav_icon} />
                <span>{getTranslation("Logout", language)}</span>
              </Button>
            </li>
          </>
        )}
      </ul>
      <ul>
        {!isToggle && !isMobile && (
          <li className={styles.notification__wrapper}>
            <Tooltip
              title={language === "ar" ? "Change Language?" : "تغيير اللغة"}
            >
              <Switch
                checked={language === "ar"}
                onChange={handleChangeLang}
                checkedChildren="English"
                unCheckedChildren="عربي"
              />
            </Tooltip>
          </li>
        )}

        {userInfo ? (
          !isToggle &&
          !isMobile && (
            <>

              <div>
                <Dropdown
                  menu={{
                    items: itemsProfile.filter(Boolean),
                  }}
                >
                  <div className={styles.profile__wrapper}>
                    <img src={avatar} alt="." />
                    <span>
                      {first_name} {last_name}
                    </span>
                    <FaAngleDown />
                  </div>
                </Dropdown>
              </div>
            </>
          )
        ) : (
          <li className={styles.btn__wrapper}>
            <Link className="primaryBtn" to="/login">
              <FiUserPlus className={styles.nav_icon} />
              {getTranslation("Login", language)}
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}

export default Navbar;
